import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  List,
  ListItem,
  Spinner,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLicenses, createLicense, type LicenseDto } from '../../../api/libraryApi';

export interface LicenseSelectProps {
  /** Currently-selected license display name (or null when cleared). */
  value: string | null;
  /** Fires with the selected license id + name, or (null, null) when cleared. */
  onChange: (licenseId: string | null, licenseName: string | null) => void;
  /** Wrap in FormControl + FormLabel. Default true. */
  wrapInFormControl?: boolean;
  /** Override the default form label text. Ignored when wrapInFormControl=false. */
  label?: string;
  /** Optional placeholder when nothing is selected. */
  placeholder?: string;
  /** Disable the entire control. */
  isDisabled?: boolean;
  /** Optional test id passed through to the input element. */
  inputTestId?: string;
}

/**
 * Single-value license combobox (016-track-licensing). Modeled on TagMultiSelect
 * but single-valued and CASE-PRESERVING — license names are never lowercased.
 * Lets the user pick an existing license (autocomplete), clear it (→ null), or
 * create a new one by typing a name not already in the list.
 */
export const LicenseSelect: React.FC<LicenseSelectProps> = ({
  value,
  onChange,
  wrapInFormControl = true,
  label,
  placeholder,
  isDisabled = false,
  inputTestId = 'license-input',
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: licenses = [] } = useQuery({
    queryKey: ['admin-licenses'],
    queryFn: fetchLicenses,
    staleTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createLicense(name),
    onSuccess: (license) => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      select(license);
    },
  });

  const select = useCallback(
    (license: LicenseDto) => {
      onChange(license.id, license.name);
      setInputValue('');
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const clear = useCallback(() => {
    onChange(null, null);
    setInputValue('');
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, [onChange]);

  const filteredSuggestions = useMemo(
    () =>
      licenses.filter(
        (l) =>
          l.name.toLowerCase().includes(inputValue.toLowerCase()) &&
          l.name.toLowerCase() !== value?.toLowerCase(),
      ),
    [licenses, inputValue, value],
  );

  // Allow creating only when the typed name doesn't already exist (case-insensitive).
  const trimmedInput = inputValue.trim();
  const exactMatch = useMemo(
    () => licenses.find((l) => l.name.toLowerCase() === trimmedInput.toLowerCase()),
    [licenses, trimmedInput],
  );
  const canCreate = trimmedInput.length > 0 && !exactMatch;

  const commitInput = useCallback(() => {
    if (!trimmedInput) return;
    if (exactMatch) {
      select(exactMatch);
      return;
    }
    if (!createMutation.isPending) {
      createMutation.mutate(trimmedInput);
    }
  }, [trimmedInput, exactMatch, select, createMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          select(filteredSuggestions[highlightedIndex]);
        } else {
          commitInput();
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev,
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    },
    [filteredSuggestions, highlightedIndex, select, commitInput],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
    setHighlightedIndex(-1);
  }, []);

  const handleBlur = useCallback(() => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, []);

  const hasValue = value != null && value !== '';
  const effectivePlaceholder =
    placeholder ?? (!hasValue ? t('admin.library.licensePlaceholder') : '');

  const control = (
    <Box
      position="relative"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={2}
      _focusWithin={{
        borderColor: 'blue.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
      }}
      cursor={isDisabled ? 'not-allowed' : 'text'}
      opacity={isDisabled ? 0.6 : 1}
      onClick={() => inputRef.current?.focus()}
      data-testid="license-select"
    >
      <Flex wrap="wrap" gap={1} align="center">
        {hasValue && (
          <Tag size="md" colorScheme="purple" borderRadius="full" data-testid="license-value">
            <TagLabel>{value}</TagLabel>
            {!isDisabled && (
              <TagCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
                aria-label={t('admin.library.licenseClear', 'Clear license')}
                data-testid="license-clear"
              />
            )}
          </Tag>
        )}
        {!hasValue && !isDisabled && (
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length > 0 && setShowSuggestions(true)}
            onBlur={handleBlur}
            placeholder={effectivePlaceholder}
            size="md"
            variant="unstyled"
            minW="120px"
            flex={1}
            data-testid={inputTestId}
          />
        )}
        {createMutation.isPending && <Spinner size="xs" data-testid="license-creating" />}
      </Flex>

      {showSuggestions && (filteredSuggestions.length > 0 || canCreate) && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={10}
          mt={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          maxH="200px"
          overflowY="auto"
          data-testid="license-suggestions"
        >
          <List spacing={0}>
            {filteredSuggestions.map((license, index) => (
              <ListItem
                key={license.id}
                px={3}
                py={1.5}
                cursor="pointer"
                bg={index === highlightedIndex ? 'blue.50' : 'transparent'}
                _hover={{ bg: 'blue.50' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(license)}
                data-testid={`license-suggestion-${license.name}`}
              >
                <Text fontSize="sm">{license.name}</Text>
              </ListItem>
            ))}
            {canCreate && (
              <ListItem
                px={3}
                py={1.5}
                cursor="pointer"
                _hover={{ bg: 'green.50' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitInput}
                data-testid="license-create-option"
              >
                <Text fontSize="sm" color="green.600">
                  {t('admin.library.licenseCreate', { name: trimmedInput })}
                </Text>
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </Box>
  );

  if (!wrapInFormControl) {
    return control;
  }

  return (
    <FormControl>
      <FormLabel>{label ?? t('admin.library.license')}</FormLabel>
      {control}
    </FormControl>
  );
};

export default LicenseSelect;
